<template>
  <div class="playback-uplot">
    <div class="playback-uplot__zoom-controls">
      <button
        type="button"
        class="playback-uplot__zoom-button"
        title="Move left"
        :disabled="!canPanLeft()"
        @click.stop="panLeft">
        <feather-icon icon="ChevronLeftIcon" svgClasses="h-3 w-3" />
      </button>
      <button
        type="button"
        class="playback-uplot__zoom-button"
        title="Zoom in"
        :disabled="!canZoomIn()"
        @click.stop="zoomIn">
        <feather-icon icon="PlusIcon" svgClasses="h-3 w-3" />
      </button>
      <button
        type="button"
        class="playback-uplot__zoom-button"
        title="Zoom out"
        :disabled="!canZoomOut()"
        @click.stop="zoomOut">
        <feather-icon icon="MinusIcon" svgClasses="h-3 w-3" />
      </button>
      <button
        type="button"
        class="playback-uplot__zoom-button"
        title="Move right"
        :disabled="!canPanRight()"
        @click.stop="panRight">
        <feather-icon icon="ChevronRightIcon" svgClasses="h-3 w-3" />
      </button>
    </div>
    <div ref="chart" class="playback-uplot__chart"></div>
    <div
      v-if="tooltip.visible"
      class="playback-uplot__tooltip"
      :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }">
      <div v-if="tooltip.isEvent" class="playback-uplot__tooltip-title">
        {{ tooltip.eventType }}
      </div>
    </div>
  </div>
</template>

<script>
import 'uplot/dist/uPlot.min.css'
import periodicOverspeed from '../../../assets/images/periodic_overspeed.png'
import periodicHarshBrk from '../../../assets/images/periodic_harsh_brk.png'
import periodicHarshAcc from '../../../assets/images/periodic_harsh_acc.png'
import periodicMbcn from '../../../assets/images/periodic_mbcn.png'
import periodicCrash from '../../../assets/images/periodic_crash.png'
import periodicGeoIn from '../../../assets/images/periodic_geo_in.png'
import periodicGeoOut from '../../../assets/images/periodic_geo_out.png'
import periodicDefault from '../../../assets/images/periodic_default.png'
import periodicOverstayOn from '../../../assets/images/periodic_overstay_on.png'
import periodicOverstayOff from '../../../assets/images/periodic_overstay_off.png'

let UPlot = null
const alertIconImages = {}
const alertIconWidth = 20
const alertIconHeight = 28

const metricConfig = {
  speed: {
    labels: ['Speed'],
    keys: ['speed'],
    colors: ['#FF5E5D'],
  },
  ignition: {
    labels: ['Ignition'],
    keys: ['ignition'],
    colors: ['#00D68F'],
  },
  accu_voltage: {
    labels: ['Accu Voltage'],
    keys: ['external_power_voltage'],
    colors: ['#7367F0'],
  },
  fuel: {
    labels: ['Fuel'],
    keys: ['fuel_level_1_x'],
    colors: ['#FFA41B'],
  },
  temperature: {
    labels: ['Chiller Temp 1', 'Chiller Temp 2'],
    keys: ['dallas_temperature_1', 'dallas_temperature_2'],
    colors: ['#FFA41B', '#00CFE8'],
  },
}

const eventColors = {
  OVERSPEED: '#EA5455',
  HARSH_BRK: '#D97706',
  HARSBREAK: '#D97706',
  HARSHBREAK: '#D97706',
  HARSH_ACC: '#F59E0B',
  MB_CN: '#7367F0',
  CRASH: '#111827',
  GEO_IN: '#22C55E',
  GEOFENCE_IN: '#22C55E',
  GEO_OUT: '#3B82F6',
  GEOFENCE_OUT: '#3B82F6',
  OVERSTAY_ENGINE_ON: '#EF4444',
  OVERSTAY_ENGINE_OFF: '#64748B',
}

const eventIcons = {
  OVERSPEED: periodicOverspeed,
  HARSH_BRK: periodicHarshBrk,
  HARSBREAK: periodicHarshBrk,
  HARSHBREAK: periodicHarshBrk,
  HARSH_ACC: periodicHarshAcc,
  MB_CN: periodicMbcn,
  CRASH: periodicCrash,
  GEO_IN: periodicGeoIn,
  GEOFENCE_IN: periodicGeoIn,
  GEO_OUT: periodicGeoOut,
  GEOFENCE_OUT: periodicGeoOut,
  OVERSTAY_ENGINE_ON: periodicOverstayOn,
  OVERSTAY_ENGINE_OFF: periodicOverstayOff,
}

export default {
  name: 'PlaybackUPlotChart',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    metric: {
      type: String,
      default: 'speed',
    },
    height: {
      type: Number,
      default: 255,
    },
    selectedIndex: {
      type: Number,
      default: 0,
    },
    activeTemperatureKeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      chartData: [],
      alertPoints: [],
      cleanupHandlers: [],
      zoomRange: null,
      tooltip: {
        visible: false,
        left: 0,
        top: 0,
        eventType: '',
        isEvent: false,
      },
    }
  },
  watch: {
    rows: {
      handler() {
        this.rebuildChart()
      },
      deep: false,
    },
    metric() {
      this.rebuildChart()
    },
    height() {
      this.rebuildChart()
    },
    activeTemperatureKeys() {
      if (this.metric === 'temperature') {
        this.rebuildChart()
      }
    },
    selectedIndex() {
      if (this.chart) {
        this.chart.redraw()
      }
    },
  },
  mounted() {
    this.preloadAlertIcons()
    this.rebuildChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.destroyChart()
  },
  methods: {
    rebuildChart() {
      this.destroyChart()

      if (!this.$refs.chart || this.rows.length === 0) {
        return
      }

      if (!UPlot) {
        try {
          UPlot = require('uplot/dist/uPlot.cjs.js')
        } catch (error) {
          this.$emit('chart-error', error)
          return
        }
      }

      const payload = this.buildData()
      this.chartData = payload.data
      this.alertPoints = payload.alertPoints
      this.zoomRange = null

      try {
        this.chart = new UPlot(
          this.buildOptions(payload.series),
          payload.data,
          this.$refs.chart
        )
      } catch (error) {
        this.$emit('chart-error', error)
        return
      }

      this.bindOverlayEvents()
    },
    destroyChart() {
      this.cleanupHandlers.forEach((cleanup) => cleanup())
      this.cleanupHandlers = []
      this.tooltip.visible = false

      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
    preloadAlertIcons() {
      const sources = Object.keys(eventIcons).map((key) => eventIcons[key])
      sources.push(periodicDefault)

      sources.forEach((src) => {
        if (alertIconImages[src]) return

        const image = new Image()
        image.onload = () => {
          if (this.chart) {
            this.chart.redraw()
          }
        }
        image.src = src
        alertIconImages[src] = image
      })
    },
    buildData() {
      const config = this.getMetricConfig()
      const xData = []
      const ySeries = config.keys.map(() => [])
      const alertPoints = []

      this.rows.forEach((row, index) => {
        const time = new Date(row.device_time).getTime() / 1000
        xData.push(time)

        config.keys.forEach((key, seriesIndex) => {
          const value = Number(row[key])
          ySeries[seriesIndex].push(Number.isFinite(value) ? value : null)
        })

        if (row.event_type && row.event_type !== 'SAMPLING') {
          const metricPoint = this.getMetricPoint(config, row)
          alertPoints.push({
            index,
            x: time,
            y: metricPoint.value,
            eventType: row.event_type,
            deviceTime: this.formatDeviceTime(row.device_time, true),
            metricLabel: metricPoint.label,
            metricValue: metricPoint.value,
            //color: eventColors[row.event_type] || '#6B7280',
            iconSrc: eventIcons[row.event_type] || periodicDefault,
          })
        }
      })

      return {
        data: [xData, ...ySeries],
        series: config.labels.map((label, index) => ({
          label,
          stroke: config.colors[index],
          width: 2,
          points: {
            show: false,
          },
        })),
        alertPoints,
      }
    },
    getMetricPoint(config, row) {
      for (let index = 0; index < config.keys.length; index++) {
        const value = Number(row[config.keys[index]])
        if (Number.isFinite(value)) {
          return {
            label: config.labels[index],
            value,
          }
        }
      }

      return {
        label: config.labels[0] || '',
        value: null,
      }
    },
    getMetricConfig() {
      const config = metricConfig[this.metric] || metricConfig.speed

      if (this.metric !== 'temperature' || this.activeTemperatureKeys.length === 0) {
        return config
      }

      const labels = []
      const keys = []
      const colors = []

      config.keys.forEach((key, index) => {
        if (this.activeTemperatureKeys.indexOf(key) !== -1) {
          labels.push(config.labels[index])
          keys.push(key)
          colors.push(config.colors[index])
        }
      })

      return keys.length > 0
        ? { labels, keys, colors }
        : config
    },
    buildOptions(series) {
      return {
        width: this.getChartWidth(),
        height: Math.max(this.height, 180),
        cursor: {
          drag: {
            setScale: false,
          },
        },
        scales: {
          x: {
            time: true,
          },
        },
        axes: [
          {
            stroke: '#6B7280',
            values: (u, values) => values.map((value) => this.formatAxisTime(value)),
            grid: {
              stroke: '#EEF0F4',
              width: 1,
            },
          },
          {
            stroke: '#6B7280',
            grid: {
              stroke: '#EEF0F4',
              width: 1,
            },
          },
        ],
        series: [
          {},
          ...series,
        ],
        hooks: {
          draw: [
            (u) => {
              this.drawSelectedLine(u)
              this.drawAlertMarkers(u)
            },
          ],
        },
      }
    },
    formatAxisTime(timestamp) {
      const index = this.findNearestIndex(timestamp)
      const row = index >= 0 ? this.rows[index] : null

      return row ? this.formatDeviceTime(row.device_time) : ''
    },
    formatDeviceTime(value, withSeconds = false) {
      if (!value) return ''

      const match = String(value).match(/(\d{2}):(\d{2})(?::(\d{2}))?/)
      if (match) {
        return withSeconds && match[3]
          ? `${match[1]}:${match[2]}:${match[3]}`
          : `${match[1]}:${match[2]}`
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)

      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return withSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`
    },
    getChartWidth() {
      return this.$refs.chart
        ? Math.max(this.$refs.chart.clientWidth, 320)
        : 320
    },
    handleResize() {
      if (!this.chart) return

      this.chart.setSize({
        width: this.getChartWidth(),
        height: Math.max(this.height, 180),
      })
    },
    getFullXRange() {
      const xData = this.chartData[0] || []
      if (xData.length < 2) return null

      return {
        min: xData[0],
        max: xData[xData.length - 1],
      }
    },
    getCurrentXRange() {
      if (this.zoomRange) return this.zoomRange

      if (this.chart && this.chart.scales && this.chart.scales.x) {
        const range = {
          min: this.chart.scales.x.min,
          max: this.chart.scales.x.max,
        }

        if (Number.isFinite(range.min) && Number.isFinite(range.max)) {
          return range
        }
      }

      return this.getFullXRange()
    },
    getMinZoomSpan(fullRange) {
      return Math.max(10, (fullRange.max - fullRange.min) / 1000)
    },
    canZoomIn() {
      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange) return false

      return (currentRange.max - currentRange.min) > this.getMinZoomSpan(fullRange)
    },
    canZoomOut() {
      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange) return false

      return currentRange.min > fullRange.min || currentRange.max < fullRange.max
    },
    canPanLeft() {
      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange || !this.canZoomOut()) return false

      return currentRange.min > fullRange.min
    },
    canPanRight() {
      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange || !this.canZoomOut()) return false

      return currentRange.max < fullRange.max
    },
    zoomIn() {
      this.zoomChart(0.5)
    },
    zoomOut() {
      this.zoomChart(2)
    },
    panLeft() {
      this.panChart(-0.4)
    },
    panRight() {
      this.panChart(0.4)
    },
    zoomChart(multiplier) {
      if (!this.chart) return

      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange) return

      const fullSpan = fullRange.max - fullRange.min
      const currentSpan = currentRange.max - currentRange.min
      const minSpan = this.getMinZoomSpan(fullRange)
      const nextSpan = Math.min(fullSpan, Math.max(minSpan, currentSpan * multiplier))
      const center = (currentRange.min + currentRange.max) / 2
      let min = center - (nextSpan / 2)
      let max = center + (nextSpan / 2)

      if (min < fullRange.min) {
        min = fullRange.min
        max = min + nextSpan
      }

      if (max > fullRange.max) {
        max = fullRange.max
        min = max - nextSpan
      }

      this.zoomRange = { min, max }
      this.tooltip.visible = false
      this.chart.setScale('x', this.zoomRange)
    },
    panChart(directionMultiplier) {
      if (!this.chart) return

      const fullRange = this.getFullXRange()
      const currentRange = this.getCurrentXRange()
      if (!fullRange || !currentRange) return

      const currentSpan = currentRange.max - currentRange.min
      const shift = currentSpan * directionMultiplier
      let min = currentRange.min + shift
      let max = currentRange.max + shift

      if (min < fullRange.min) {
        min = fullRange.min
        max = min + currentSpan
      }

      if (max > fullRange.max) {
        max = fullRange.max
        min = max - currentSpan
      }

      this.zoomRange = { min, max }
      this.tooltip.visible = false
      this.chart.setScale('x', this.zoomRange)
    },
    bindOverlayEvents() {
      const over = this.chart && this.chart.over
      if (!over) return

      const clickHandler = (event) => {
        const rect = this.chart.over.getBoundingClientRect()
        const nearestAlert = this.getNearestAlertPoint(event.clientX - rect.left, 12)
        const index = nearestAlert
          ? nearestAlert.index
          : this.getNearestIndexFromEvent(event)

        if (index >= 0) {
          this.$emit('select-index', index)
        }
      }

      const moveHandler = (event) => {
        this.updateTooltip(event)
      }

      const leaveHandler = () => {
        this.tooltip.visible = false
      }

      over.addEventListener('click', clickHandler)
      over.addEventListener('mousemove', moveHandler)
      over.addEventListener('mouseleave', leaveHandler)

      this.cleanupHandlers.push(() => {
        over.removeEventListener('click', clickHandler)
        over.removeEventListener('mousemove', moveHandler)
        over.removeEventListener('mouseleave', leaveHandler)
      })
    },
    getNearestIndexFromEvent(event) {
      if (!this.chart || this.chartData.length === 0) return -1

      const rect = this.chart.over.getBoundingClientRect()
      const xValue = this.chart.posToVal(event.clientX - rect.left, 'x')
      return this.findNearestIndex(xValue)
    },
    findNearestIndex(xValue) {
      const xData = this.chartData[0] || []
      if (xData.length === 0) return -1

      let low = 0
      let high = xData.length - 1

      while (low < high) {
        const mid = Math.floor((low + high) / 2)
        if (xData[mid] < xValue) low = mid + 1
        else high = mid
      }

      if (low === 0) return 0
      const prev = low - 1
      return Math.abs(xData[prev] - xValue) <= Math.abs(xData[low] - xValue)
        ? prev
        : low
    },
    drawSelectedLine(u) {
      const xData = this.chartData[0] || []
      if (!xData[this.selectedIndex]) return

      const ctx = u.ctx
      const x = Math.round(u.valToPos(xData[this.selectedIndex], 'x', true))
      const top = u.bbox.top
      const bottom = u.bbox.top + u.bbox.height
      const selectedY = this.getSelectedMetricValue()

      ctx.save()
      ctx.beginPath()
      ctx.strokeStyle = '#775DD0'
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.moveTo(x, top)
      ctx.lineTo(x, bottom)
      ctx.stroke()

      if (Number.isFinite(selectedY)) {
        const y = u.valToPos(selectedY, 'y', true)

        if (y >= top && y <= bottom) {
          ctx.setLineDash([])
          ctx.beginPath()
          ctx.fillStyle = '#111827'
          ctx.strokeStyle = '#FFFFFF'
          ctx.lineWidth = 2
          ctx.arc(x, y, 5, 0, Math.PI * 2)
          ctx.fill()
          ctx.stroke()
        }
      }

      ctx.restore()
    },
    getSelectedMetricValue() {
      for (let seriesIndex = 1; seriesIndex < this.chartData.length; seriesIndex++) {
        const value = this.chartData[seriesIndex][this.selectedIndex]
        if (Number.isFinite(value)) {
          return value
        }
      }

      return null
    },
    drawAlertMarkers(u) {
      if (this.alertPoints.length === 0) return

      const ctx = u.ctx

      ctx.save()
      this.alertPoints.forEach((point) => {
        const x = u.valToPos(point.x, 'x', true)

        if (x < u.bbox.left || x > u.bbox.left + u.bbox.width) return

        if (Number.isFinite(point.y)) {
          const y = u.valToPos(point.y, 'y', true)
          ctx.globalAlpha = 1
          ctx.beginPath()
          ctx.fillStyle = point.color
          ctx.arc(x, y, 4, 0, Math.PI * 2)
          ctx.fill()
        }

        this.drawAlertIcon(ctx, u, point, x)
      })
      ctx.restore()
    },
    drawAlertIcon(ctx, u, point, x) {
      const image = alertIconImages[point.iconSrc]
      if (!image || !image.complete || image.naturalWidth === 0) return

      const hasMetricPosition = Number.isFinite(point.y)
      const y = hasMetricPosition
        ? u.valToPos(point.y, 'y', true) - alertIconHeight - 8
        : u.bbox.top + 4
      const iconX = x - (alertIconWidth / 2)
      const iconY = Math.max(
        u.bbox.top + 2,
        Math.min(y, u.bbox.top + u.bbox.height - alertIconHeight)
      )

      ctx.globalAlpha = 1
      ctx.drawImage(image, iconX, iconY, alertIconWidth, alertIconHeight)
    },
    updateTooltip(event) {
      if (!this.chart || this.chartData.length === 0) {
        this.tooltip.visible = false
        return
      }

      const rect = this.chart.over.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      const mouseY = event.clientY - rect.top
      const nearestAlert = this.getNearestAlertPoint(mouseX, 10)

      if (nearestAlert) {
        this.tooltip = {
          visible: true,
          left: this.getTooltipLeft(mouseX, rect.width),
          top: this.getTooltipTop(mouseY),
          eventType: nearestAlert.eventType,
          isEvent: true,
        }
        return
      }

      this.tooltip.visible = false
    },
    getNearestAlertPoint(mouseX, threshold = 8) {
      if (this.alertPoints.length === 0) return null

      let nearest = null
      let nearestDistance = Infinity

      this.alertPoints.forEach((point) => {
        const x = this.chart.valToPos(point.x, 'x', true) - this.chart.bbox.left
        const distance = Math.abs(mouseX - x)

        if (distance < nearestDistance) {
          nearestDistance = distance
          nearest = point
        }
      })

      return nearestDistance <= threshold ? nearest : null
    },
    getTooltipLeft(mouseX, chartWidth) {
      return Math.max(8, Math.min(mouseX + 12, chartWidth - 190))
    },
    getTooltipTop(mouseY) {
      return Math.max(mouseY - 12, 8)
    },
  },
}
</script>

<style scoped>
.playback-uplot {
  position: relative;
  width: 100%;
  min-height: 180px;
}

.playback-uplot__zoom-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 4;
  display: flex;
  overflow: hidden;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.playback-uplot__zoom-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-right: 1px solid #E5E7EB;
  background: transparent;
  color: #374151;
  cursor: pointer;
}

.playback-uplot__zoom-button:last-child {
  border-right: 0;
}

.playback-uplot__zoom-button:hover:not(:disabled) {
  background: #F9FAFB;
  color: #EA5455;
}

.playback-uplot__zoom-button:disabled {
  color: #C7CBD1;
  cursor: not-allowed;
}

.playback-uplot__chart {
  width: 100%;
}

.playback-uplot__tooltip {
  position: absolute;
  z-index: 5;
  min-width: 168px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  pointer-events: none;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.2);
}

.playback-uplot__tooltip-title {
  font-weight: 600;
}
</style>
